import type { BaseSyntheticEvent } from "react";

import { Helmet } from "react-helmet";

import { Button } from "@alpacahq/ui/old";

import { FullScreenSpinner } from "~/v2/components/common";
import {
  ApplicationDetails,
  type ApplicationDetailsProps,
} from "./ApplicationDetails.pres";
import {
  GraphicsFields,
  type GraphicsFieldsProps,
} from "./GraphicsFields/GraphicsFields.connector";
import { PublishFooter, type PublishFooterProps } from "./PublishFooter.pres";
import type { SubmitAppMode } from "./SubmitApp.types";
import {
  OAUTH_DOCS_URL,
  SubmitAppModal,
  type SubmitAppModalProps,
} from "./SubmitAppModal.pres";

export interface SubmitAppPageProps {
  mode: SubmitAppMode;
  isLoading: boolean;
  hasEditLoadError: boolean;
  isRetryingEditLoad: boolean;
  onSubmit: (event?: BaseSyntheticEvent) => Promise<void>;
  onBack: () => void;
  onRetryEditLoad: () => void;
}

export interface SubmitAppPresentationProps {
  pageProps: SubmitAppPageProps;
  applicationDetailsProps: ApplicationDetailsProps;
  graphicsFieldsProps: GraphicsFieldsProps;
  publishFooterProps: PublishFooterProps;
  modalProps: SubmitAppModalProps;
}

export const SubmitAppPresentation = ({
  pageProps,
  applicationDetailsProps,
  graphicsFieldsProps,
  publishFooterProps,
  modalProps,
}: SubmitAppPresentationProps) => (
  <>
    <Helmet title="Alpaca Connect" />
    {pageProps.isLoading ? (
      <FullScreenSpinner />
    ) : pageProps.hasEditLoadError ? (
      <main className="px-4 py-8 sm:px-8">
        <div className="max-w-xl" role="alert">
          <h1 className="text-base">
            We couldn&apos;t load this app. Please try again.
          </h1>
          <div className="mt-4 flex gap-2">
            <Button
              onClick={pageProps.onBack}
              type="button"
              variant="alternate"
            >
              <span aria-hidden="true">{"< "}</span>
              Back
            </Button>
            <Button
              disabled={pageProps.isRetryingEditLoad}
              loading={pageProps.isRetryingEditLoad}
              loadingPlaceholder="Retrying..."
              onClick={pageProps.onRetryEditLoad}
              type="button"
            >
              Retry
            </Button>
          </div>
        </div>
      </main>
    ) : (
      <main className="px-4 py-8 sm:px-8">
        <Button onClick={pageProps.onBack} type="button" variant="alternate">
          <span aria-hidden="true">{"< "}</span>
          Back
        </Button>
        <h1 className="my-8 text-3xl">
          {pageProps.mode === "edit" ? "Edit Your App" : "Connect Your App"}
        </h1>

        <form onSubmit={pageProps.onSubmit}>
          <div className="flex max-w-xl flex-col gap-12">
            <ApplicationDetails {...applicationDetailsProps} />

            <section className="flex flex-col gap-4">
              <h2 className="mb-0 text-2xl">FAQ</h2>
              <ul className="flex flex-col gap-4">
                <li className="flex flex-col gap-1">
                  <p className="mb-0 font-semibold">
                    Who will be reaching out regarding my application?
                  </p>
                  <p className="mb-0">
                    You should receive an email from Alpaca&apos;s support team,{" "}
                    <a href="mailto:support@alpaca.markets">
                      support@alpaca.markets
                    </a>{" "}
                    within the next 24 business hours after application
                    submission.
                  </p>
                </li>
                <li className="flex flex-col gap-1">
                  <p className="mb-0 font-semibold">
                    What is the current approval timeframe?
                  </p>
                  <p className="mb-0">
                    You should expect the timeframe between application
                    submission and your app going live to be around 30 business
                    days.
                  </p>
                </li>
                <li className="flex flex-col gap-1">
                  <p className="mb-0 font-semibold">
                    What is the next step after I submit my application?
                  </p>
                  <p className="mb-0">
                    Please connect your app to your paper account using your
                    Client ID credentials provided and the information provided{" "}
                    <a
                      href={OAUTH_DOCS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="OAuth integration documentation"
                    >
                      HERE
                    </a>
                    .
                  </p>
                </li>
              </ul>
            </section>

            <GraphicsFields {...graphicsFieldsProps} />

            <PublishFooter {...publishFooterProps} />
          </div>
        </form>
      </main>
    )}

    <SubmitAppModal {...modalProps} />
  </>
);
